import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState, useCallback } from 'react';
import { SubscriptionState, DeliveryTimeValue, Subscription } from '../App/useSubscription';
import { useEmitMessage } from '../common/useEmitMessage';
import { useFetch } from '../common/useFetch';
import ja from 'dayjs/locale/ja';
import { formatDeliveryDateFromDayjs } from '../../utils/format';

dayjs.locale(ja);

/**
 * 現在の配送日時・配送可能曜日の一覧を担う
 */
export const useDeliveryDate = (
  subscription: SubscriptionState,
  token: string,
  successCallback?: () => void,
) => {

  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');
  const success = () => {
    if (successCallback) successCallback();
    emitMessage('app-modify-deliverydate');
  }

  const [result, setResult] = useState<DeliveryDates>({});
  const [updateDeliveryDaysState, callUpdateDeliveryDays] =
    useFetch('updateDeliveryDays', token, success, failed);

  // お届け予定日更新関数
  const updateDeliveryDays = useCallback((
    nextDeliveryDate: Date,
    time: DeliveryTimeValue
  ) => {
    if (!subscription) return;

    const params: UpdateDeliveryDaysParams = {
      subscriptionId: subscription.subscriptionContractId,
      deliveryDays: dayjs(nextDeliveryDate).diff(dayjs(subscription.nextBillingDate), 'day'),
      deliveryTime: time,
    }

    // お届け日時変更に合わせて注文日も更新する、にチェックが入っている場合。
    if (subscription.shop.changeNextBillingDateWithDeliveryDate) {
      if (!result.nextDeliveryDate) return;
      // 次の注文日。次に希望する配送日時から管理画面設定値の"配送までの日数"を引いたもの
      params.nextBilling = dayjs(nextDeliveryDate).subtract(subscription.shop.deliveryDays, 'day');
      // 配送日指定なし設定が有効の場合は-1を仕様として追加し、
      // そうでない場合は管理画面で設定したお届け日時に変更する(注文日がずれるため)
      params.deliveryDays = (subscription.deliveryDays === -1 && subscription.shop.enableDeliveryDateNoDay)
        ? -1 : subscription.shop.deliveryDays;
      // Shopify Flowのトリガーとしてお届け日変更のトリガーを発行する場合に以下情報が必要
      params.deliveryDate = formatDeliveryDateFromDayjs(result.nextDeliveryDate);
    }

    emitMessage('app-updating-deliverydate', 'keep');
    callUpdateDeliveryDays(params);
  }, [subscription, result]);

  // 諸々の日時
  useEffect(() => {
    if (!subscription) return;

    const modifyLimitDate = 
      dayjs(subscription.nextBillingDate)
        .subtract(subscription.shop.allowModifyDays || 0, 'day');

    const nextBillingDate = dayjs(subscription.nextBillingDate);

    const deliveryDays = subscription.deliveryDays === -1
      ? subscription.shop.deliveryDays
      : subscription.deliveryDays;
    const disableDeliveryWeeks = createDisableDeliveryWeeks(subscription);
    const nextDeliveryDate = createNextDeliveryDate(nextBillingDate, deliveryDays, disableDeliveryWeeks);

    let nextDeliveryMaxDate: (dayjs.Dayjs | undefined) = nextBillingDate.add(30, 'day');
    if (subscription.shop.changeNextBillingDateWithDeliveryDate) {
      const nextDeliveryLimitDate = createNextDeliveryDate(
        nextBillingDate, subscription.shop.deliveryDaysMax, disableDeliveryWeeks
      );
      const nextBillingLimitDate = nextBillingDate.add(
        subscription.billingPolicyIntervalCount,
        subscription.billingPolicyInterval.toLocaleLowerCase() as 'day' | 'week' | 'month'
      );
      nextDeliveryMaxDate = 
        subscription.shop.deliveryDaysMax === 0
          ? undefined
          : nextBillingLimitDate.isAfter(nextDeliveryLimitDate)
            ? nextDeliveryLimitDate : nextBillingLimitDate;
    }

    let nextDeliveryMinDate = nextBillingDate.add(subscription.shop.deliveryDays, 'day'); // huckleBerryではdeliveryDaysに+1をしていたが、不適と思い除外
    if (subscription.shop.changeNextBillingDateWithDeliveryDate) {
      const purchasedDate = dayjs(
        subscription.billingAttempts.length
          ? subscription.billingAttempts[0].createdAt
          : subscription.createdAt
      );
      nextDeliveryMinDate =
        dayjs()
          .add(subscription.shop.deliveryDays, 'day')
          .isAfter(purchasedDate.add(subscription.shop.deliveryDays, 'day'))
            ? dayjs().add(subscription.shop.deliveryDays, 'day') // huckleBerryではdeliveryDaysに+1をしていたが、不適と思い除外
            : purchasedDate.add(subscription.shop.deliveryDays, 'day');
    }

    const enabledDeliveryTimes = createEnabledDeliveryTimes(subscription);

    // 「スキップ、一時停止、解約、商品の変更 / 追加 / 削除、プラン変更、お届け日の変更可能日設定」
    // が有効な場合、現在変更可能かどうかのフラグ
    const isAfterLimitDate = modifyLimitDate ? dayjs().isAfter(modifyLimitDate) : false;
    const isBanningModify = subscription?.shop.isAllowModifyDays && isAfterLimitDate;

    setResult({
      ...result,
      modifyLimitDate,
      nextBillingDate,
      nextDeliveryDate,
      nextDeliveryMinDate,
      nextDeliveryMaxDate,
      disableDeliveryWeeks,
      enabledDeliveryTimes,
      isBanningModify
    });

  },[subscription]);

  return [
    { dates: result, loadingUpdateDeliveryDays: updateDeliveryDaysState.loading },
    updateDeliveryDays
  ] as [
    { dates: DeliveryDates, loadingUpdateDeliveryDays: boolean },
    Function
  ];
}

const createNextDeliveryDate = (
  nextBillingDate: dayjs.Dayjs,
  deliveryDays: number,
  disableDeliveryWeeks: { value: string, disabled: boolean }[]
): dayjs.Dayjs => {

  let result = dayjs(nextBillingDate).add(deliveryDays, 'day');

  if (!disableDeliveryWeeks.every((item) => item.disabled)) {
    const index = result.day();
    const _first = disableDeliveryWeeks.slice(index);
    const _rest = disableDeliveryWeeks.slice(0, index);
    const disableDays = _first.concat(_rest);
    const add = disableDays.findIndex((item) => !item.disabled);
    result = result.add(add, 'days');
  }

  return result;
}

const createDisableDeliveryWeeks = (subscription: Subscription) => {
  return [
    { value: "Sun", disabled: subscription.shop.disableDeliveryDaysSun },
    { value: "Mon", disabled: subscription.shop.disableDeliveryDaysMon },
    { value: "Tue", disabled: subscription.shop.disableDeliveryDaysTue },
    { value: "Wed", disabled: subscription.shop.disableDeliveryDaysWed },
    { value: "Thr", disabled: subscription.shop.disableDeliveryDaysThr },
    { value: "Fri", disabled: subscription.shop.disableDeliveryDaysFri },
    { value: "Sat", disabled: subscription.shop.disableDeliveryDaysSat },
  ]
}

const createEnabledDeliveryTimes = (subscription: Subscription) => {
  const selectedDeliveryTime = 
    subscription.deliveryTime ? subscription.deliveryTime : 'AM';

  // 管理画面設定値がsubscription.shop.deliveryTimesに含まれているので
  // そちらを利用して表示用の配列を作成、それを返す
  return subscription.shop.deliveryTimes.map(time => {
    return {
      label: time.title,
      value: time.tag,
      selected: selectedDeliveryTime === time.tag
    }
  });
}

type DisableDeliveryWeek = {
  value: string;
  disabled: boolean;
}

type DeliveryTime = {
  label: string;
  value: string;
  selected: boolean;
}

export type DeliveryDates = {
  modifyLimitDate?: dayjs.Dayjs,
  nextBillingDate?: dayjs.Dayjs,
  nextDeliveryDate?: dayjs.Dayjs,
  nextDeliveryMinDate?: dayjs.Dayjs,
  nextDeliveryMaxDate?: dayjs.Dayjs,
  disableDeliveryWeeks?: DisableDeliveryWeek[],
  enabledDeliveryTimes?: DeliveryTime[],
  isBanningModify?: boolean
}

type UpdateDeliveryDaysParams = {
  subscriptionId: string,
  deliveryDays: number,
  nextBilling?: Dayjs,
  deliveryTime: string,
  deliveryDate?: string
}